// ⚠️ DEPRECATED — Etappe 4.4 (2026-06-27): Marker-fixer abgelöst durch
// LocationPicker + Audit-Trail in jeder Audit-Tab (karttipps/foreningar/tjanstekort/vardagskort)
// im AdminPanel. Diese Datei und die zugehörigen Endpoints /api/mod/platser/* werden
// nach 1-2 Wochen Beobachtung sauber gelöscht, falls keine Funktion vermisst wird.
// Verwendung von Filter "Bara utan koordinater" in den Audit-Tabs ersetzt die zentrale Übersicht.
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapPin, Check, X, Search, ExternalLink, Trash2, Link as LinkIcon, Move } from "lucide-react";
import FlyttaMarkorDialog from "@/components/FlyttaMarkorDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

/** Extrahera (lat, lng) eller plus-code från Google Maps / maps.app URL. */
function parseMapInput(raw) {
  if (!raw) return null;
  const str = raw.trim();
  // 1. rent plus-code (kort eller full): [2-8 alphanum]+ [2-3 alphanum]
  const plusMatch = str.match(/^[2-9CFGHJMPQRVWX]{4,8}\+[2-9CFGHJMPQRVWX]{2,3}$/i);
  if (plusMatch) return { plus_code: str.toUpperCase() };
  // 2. lat,lng
  const llMatch = str.match(/^(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)$/);
  if (llMatch) return { lat: parseFloat(llMatch[1]), lng: parseFloat(llMatch[2]) };
  // 3. Google Maps URLs
  //    https://www.google.com/maps/@63.8120,16.4060,15z
  //    https://www.google.com/maps/search/?api=1&query=63.81,16.40
  //    https://www.google.com/maps/place/.../@63.81,16.40,15z
  //    https://goo.gl/maps/... (redirect, not parsable client-side)
  //    https://maps.app.goo.gl/... (short, not parsable)
  const at = str.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (at) return { lat: parseFloat(at[1]), lng: parseFloat(at[2]) };
  const q = str.match(/[?&]q=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (q) return { lat: parseFloat(q[1]), lng: parseFloat(q[2]) };
  const query = str.match(/[?&]query=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (query) return { lat: parseFloat(query[1]), lng: parseFloat(query[2]) };
  // 4. URL that contains plus code like: maps.google.com/?q=RC64%2BR6+Backe
  const urlPlus = str.match(/[?&=\/]([2-9CFGHJMPQRVWX]{4,8}%?\+[2-9CFGHJMPQRVWX]{2,3})/i);
  if (urlPlus) return { plus_code: decodeURIComponent(urlPlus[1]).toUpperCase() };
  return null;
}

const TYP_LABEL = {
  tjanstekort: "Företag",
  foreningar: "Förening",
  karttipps: "Karttips",
  vardagskort: "Vardagskort",
};
const TYP_COLOR = {
  tjanstekort: "bg-[#4a6075]",
  foreningar:  "bg-[#6b4a22]",
  karttipps:   "bg-falu",
  vardagskort: "bg-pine",
};

export default function PlatserAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("missing"); // missing | all | placed
  const [typ, setTyp] = useState("all");
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState({}); // id → { plus_code, adress, saving?, error?, success? }
  const [moving, setMoving] = useState(null); // { kind, item }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mod/platser`, { headers: modHeaders() });
      setItems(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return items.filter(r => {
      if (filter === "missing" && r.lat != null) return false;
      if (filter === "placed" && r.lat == null) return false;
      if (typ !== "all" && r.typ !== typ) return false;
      if (search.trim() && !r.namn.toLowerCase().includes(search.trim().toLowerCase())) return false;
      return true;
    });
  }, [items, filter, typ, search]);

  const counts = useMemo(() => ({
    missing: items.filter(r => r.lat == null).length,
    placed: items.filter(r => r.lat != null).length,
    total: items.length,
  }), [items]);

  const updateEdit = (id, key, val) =>
    setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: val, success: false, error: null } }));

  const save = async (row) => {
    const e = edits[row.id] || {};
    const rawInput = (e.plus_code || "").trim();
    const parsed = rawInput ? parseMapInput(rawInput) : null;
    if (!rawInput && (e.adress ?? row.adress ?? "") === (row.adress ?? "")) return;
    setEdits(prev => ({ ...prev, [row.id]: { ...e, saving: true, error: null } }));
    try {
      const payload = {};
      if (parsed) {
        if (parsed.plus_code) payload.plus_code = parsed.plus_code;
        if (parsed.lat != null) { payload.lat = parsed.lat; payload.lng = parsed.lng; }
      } else if (rawInput) {
        payload.plus_code = rawInput; // let backend try anyway (it may error)
      }
      if (e.adress != null && e.adress !== row.adress) payload.adress = e.adress;
      await axios.post(
        `${API}/mod/platser/${row.typ}/${row.id}/set-coords`,
        payload,
        { headers: modHeaders() }
      );
      setEdits(prev => ({ ...prev, [row.id]: { ...e, saving: false, success: true, plus_code: "" } }));
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Fel vid sparande";
      setEdits(prev => ({ ...prev, [row.id]: { ...e, saving: false, error: msg } }));
    }
  };

  const clearCoords = async (row) => {
    if (!window.confirm(`Ta bort koordinater för "${row.namn}"?`)) return;
    try {
      await axios.post(
        `${API}/mod/platser/${row.typ}/${row.id}/clear-coords`,
        {},
        { headers: modHeaders() }
      );
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Fel");
    }
  };

  return (
    <div data-testid="platser-admin" className="bg-parchment rounded-lg border border-warm-border p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-xl font-semibold text-ink flex items-center gap-2">
            <MapPin size={18} className="text-falu" /> Platser &amp; Plus-koder
          </h2>
          <p className="text-xs text-bark/70 mt-0.5">
            Klistra in en <strong>plusskod</strong> (t.ex. <code className="bg-bone-alt px-1 rounded">RC64+R6</code>),
            en <strong>Google Maps-länk</strong> (t.ex. <code className="bg-bone-alt px-1 rounded">maps.google.com/@63.81,16.40,...</code>)
            eller <strong>lat,lng</strong> (t.ex. <code className="bg-bone-alt px-1 rounded">63.812, 16.405</code>).
            Systemet räknar ut resten.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterBtn active={filter === "missing"} onClick={() => setFilter("missing")}>
            Saknar koordinater ({counts.missing})
          </FilterBtn>
          <FilterBtn active={filter === "placed"} onClick={() => setFilter("placed")}>
            Placerade ({counts.placed})
          </FilterBtn>
          <FilterBtn active={filter === "all"} onClick={() => setFilter("all")}>
            Alla ({counts.total})
          </FilterBtn>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-bark/40" />
          <input
            data-testid="platser-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sök namn…"
            className="w-full pl-9 pr-3 py-2 border border-warm-border rounded-sm text-sm bg-bone focus:border-falu focus:outline-none"
          />
        </div>
        <select
          data-testid="platser-typ"
          value={typ}
          onChange={(e) => setTyp(e.target.value)}
          className="px-3 py-2 border border-warm-border rounded-sm text-sm bg-bone focus:border-falu focus:outline-none"
        >
          <option value="all">Alla typer</option>
          <option value="tjanstekort">Företag</option>
          <option value="foreningar">Föreningar</option>
          <option value="karttipps">Karttips / sevärdheter</option>
          <option value="vardagskort">Vardagskort</option>
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-bark/60 py-6">Laddar…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-bark/60 py-6">Inga poster matchar filtret.</p>
      ) : (
        <div data-testid="platser-list" className="flex flex-col gap-2">
          {filtered.map((row) => {
            const e = edits[row.id] || {};
            const placed = row.lat != null;
            return (
              <div
                key={`${row.typ}-${row.id}`}
                data-testid={`platser-row-${row.id}`}
                className={`bg-white border rounded-sm p-3 ${placed ? "border-pine/30" : "border-warm-border"}`}
              >
                <div className="flex flex-wrap items-start gap-3">
                  <span className={`shrink-0 ${TYP_COLOR[row.typ] || "bg-bark"} text-parchment text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-1`}>
                    {TYP_LABEL[row.typ] || row.typ}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink leading-tight">{row.namn}</p>
                    <p className="text-xs text-bark/60 mt-0.5">
                      {row.ort || "Ej angiven ort"}
                      {row.adress && <> &middot; {row.adress}</>}
                      {placed && <span className="text-pine"> &middot; {row.lat.toFixed(5)}, {row.lng.toFixed(5)}</span>}
                    </p>
                  </div>
                  {placed && (
                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${row.lat},${row.lng}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs text-falu hover:text-falu-hover no-underline inline-flex items-center gap-1"
                      >
                        Visa <ExternalLink size={11} />
                      </a>
                      <button
                        data-testid={`platser-move-${row.id}`}
                        onClick={() => setMoving({ kind: row.typ, item: row })}
                        className="text-xs text-pine hover:text-pine-hover inline-flex items-center gap-1"
                        title="Flytta markör manuellt"
                      >
                        <Move size={13} /> Flytta
                      </button>
                      <button
                        data-testid={`platser-clear-${row.id}`}
                        onClick={() => clearCoords(row)}
                        className="text-xs text-bark/50 hover:text-falu inline-flex items-center gap-1"
                        title="Ta bort koordinater"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                  {!placed && (
                    <button
                      data-testid={`platser-place-manual-${row.id}`}
                      onClick={() => setMoving({ kind: row.typ, item: row })}
                      className="text-xs text-pine hover:text-pine-hover inline-flex items-center gap-1 shrink-0"
                      title="Placera markör manuellt på karta"
                    >
                      <Move size={13} /> Placera på karta
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr_auto] gap-2 mt-3 items-start">
                  <div>
                    <input
                      data-testid={`platser-pluscode-${row.id}`}
                      type="text"
                      value={e.plus_code || ""}
                      onChange={(ev) => updateEdit(row.id, "plus_code", ev.target.value)}
                      onPaste={(ev) => {
                        // Let React handle default paste; preview happens via render
                        setTimeout(() => {
                          const val = ev.target.value;
                          if (val && val.length > 16) {
                            // Likely a URL paste – parse and show preview
                            updateEdit(row.id, "plus_code", val);
                          }
                        }, 10);
                      }}
                      placeholder="Plusskod el. Google Maps-länk"
                      className="w-full px-3 py-2 border border-warm-border rounded-sm text-sm bg-bone focus:border-falu focus:outline-none font-body"
                    />
                    {e.plus_code && (() => {
                      const p = parseMapInput(e.plus_code.trim());
                      if (!p) return (
                        <p className="text-[11px] text-bark/50 mt-1 flex items-center gap-1">
                          <LinkIcon size={10} /> Klistra in plusskod (t.ex. RC64+R6), lat,lng eller Google Maps-länk
                        </p>
                      );
                      if (p.plus_code) return (
                        <p className="text-[11px] text-pine mt-1 flex items-center gap-1">
                          <Check size={10} /> Plusskod: {p.plus_code}
                        </p>
                      );
                      return (
                        <p className="text-[11px] text-pine mt-1 flex items-center gap-1">
                          <Check size={10} /> {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                        </p>
                      );
                    })()}
                  </div>
                  <input
                    data-testid={`platser-adress-${row.id}`}
                    type="text"
                    value={e.adress ?? row.adress ?? ""}
                    onChange={(ev) => updateEdit(row.id, "adress", ev.target.value)}
                    placeholder="Adress (valfri)"
                    className="px-3 py-2 border border-warm-border rounded-sm text-sm bg-bone focus:border-falu focus:outline-none"
                  />
                  <button
                    data-testid={`platser-save-${row.id}`}
                    onClick={() => save(row)}
                    disabled={e.saving || (!e.plus_code && (e.adress ?? row.adress ?? "") === (row.adress ?? ""))}
                    className="flex items-center justify-center gap-1.5 bg-falu hover:bg-falu-hover disabled:opacity-40 text-parchment text-sm font-semibold px-4 py-2 rounded-sm transition-colors h-10"
                  >
                    {e.saving ? "Sparar..." : e.success ? <><Check size={14} /> Sparat</> : "Placera"}
                  </button>
                </div>
                {e.error && (
                  <p className="text-xs text-falu mt-2 flex items-center gap-1.5">
                    <X size={12} /> {e.error}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {moving && (
        <FlyttaMarkorDialog
          kind={moving.kind}
          itemId={moving.item.id}
          itemName={moving.item.namn}
          lat={moving.item.lat}
          lng={moving.item.lng}
          onClose={() => setMoving(null)}
          onSaved={() => { setMoving(null); load(); }}
        />
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
        active ? "bg-falu text-parchment" : "bg-bone border border-warm-border text-bark hover:border-bark/40"
      }`}
    >
      {children}
    </button>
  );
}
